/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import DateFnsUtils from '@date-io/date-fns';
import { toast } from 'react-toastify';
import { Modal, Fade, Backdrop, TextField } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';

import format from 'date-fns/format';
import { Formik, Field } from 'formik';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';

import { editPatient } from '../store/actions/patients';
import { getGroups, getAdmin } from '../store/actions/admin';
import AlertingAppReminderModal from './AlertingAppReminderModal';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 18,
    margin: 0,
    color: '#1e3f9e',
  },
  subtitle: {
    margin: 0,
    fontWeight: 'normal',
    color: '#888',
    fontSize: 12,
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    padding: '2rem',
    paddingTop: '1rem',
    minWidth: 550,
    maxWidth: '700px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[5],
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  dropdown: {
    width: '100%',
    margin: '30px 0 10px',
    color: 'black',
  },
  horizontalRule: {
    width: '100%',
  },
  sectionHead: {
    width: '100%',
    borderBottom: 'solid 2px gray',
    color: 'gray',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '2rem',
    width: '100%',
    justifyContent: 'space-around',
  },
  button: {
    width: '30%',
    padding: '0.6rem',
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  header: {
    width: '60%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignContent: 'center',
    marginBottom: '1rem',
  },
  form: {
    margin: '1rem',
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    width: '50%',
    alignItems: 'center',
  },
  fieldContainer: {
    width: '40%',
    marginLeft: '5%',
    marginRight: '5%',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  field: {
    width: '100%',
  },
  error: {
    color: 'red',
    marginLeft: '1rem',
  },
  nameFieldContainer: {
    width: '80%',
    marginLeft: '5%',
    marginRight: '5%',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    flexFlow: 'column',
  },
}));

const ExistingPatientModal = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const patient = useSelector((state) => state.patients.currentPatient);
  const groups = useSelector((state) => state.admin.groupsList);
  const users = useSelector((state) => state.admin.usersList);
  const { id } = useParams();
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedHCPs, setSelectedHCPs] = useState([]);
  const [hasNotifications, setHasNotifications] = useState(false);
  const [showAlertingReminder, setShowAlertingReminder] = useState(false);
  const [initialNotifyUserIds, setInitialNotifyUserIds] = useState(new Set());

  useEffect(() => {
    if (!users.length) dispatch(getAdmin());
    if (!groups.length) dispatch(getGroups());
    if (patient.notify_users) setSelectedHCPs(patient.notify_users);
    if (patient.notify_groups) setSelectedGroups(patient.notify_groups);
    setInitialNotifyUserIds(new Set((patient.notify_users || []).map((user) => user.id)));
  }, [open]);

  useEffect(() => {
    if (users.length) {
      setFilteredUsers(users.filter((user) => user.role));
    }
  }, [users]);

  useEffect(() => {
    setHasNotifications(selectedGroups.length || selectedHCPs.length);
  }, [selectedGroups, selectedHCPs]);

  const handleClose = () => {
    setSelectedGroups([]);
    setSelectedHCPs([]);
    setOpen(false);
  };

  const getLabel = (value) => {
    return `${value.first_name} ${value.last_name}`;
  };

  const submitHandler = (values) => {
    const body = { ...values };
    body.notify_users = selectedHCPs.map((hcp) => hcp.id);
    body.notify_groups = selectedGroups.map((group) => group.id);
    dispatch(editPatient(body, id));
    setOpen(false);
    toast.success('Patient changes saved. Refresh page if you do not immediately see changes.', {
      position: 'bottom-left',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      theme: 'colored',
    });
    const hasNewProvider = selectedHCPs.some((hcp) => !initialNotifyUserIds.has(hcp.id));
    if (hasNewProvider) setShowAlertingReminder(true);
  };

  return (
    <>
      <AlertingAppReminderModal open={showAlertingReminder} onClose={() => setShowAlertingReminder(false)} />
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <h2 className={classes.title}>Edit Patient Information</h2>
          <Formik
            initialValues={{
              dob: patient.dob,
              first_name: patient.first_name,
              middle_name: patient.middle_name,
              last_name: patient.last_name,
              room: patient.room,
              unit: patient.unit,
              mrn: patient.mrn,
            }}
            validate={(values) => {
              const errors = {};
              if (!values.first_name) {
                errors.first_name = '*Required';
              }
              if (!values.last_name) {
                errors.last_name = '*Required';
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                submitHandler(values);
                setSubmitting(false);
              }, 400);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              /* and other goodies */
            }) => {
              return (
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <form className={classes.form} onSubmit={handleSubmit}>
                    <div className={classes.sectionHead}>Patient Details</div>
                    <div className={classes.column}>
                      <div className={classes.nameFieldContainer}>
                        <TextField
                          className={classes.field}
                          placeholder="Enter First Name"
                          variant="outlined"
                          name="first_name"
                          value={values.first_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.first_name && touched.first_name && errors.first_name && (
                          <p className={classes.error}>
                            {errors.first_name && touched.first_name && errors.first_name}
                          </p>
                        )}
                      </div>
                      <div className={classes.nameFieldContainer}>
                        <TextField
                          className={classes.field}
                          placeholder="Enter Middle Name"
                          variant="outlined"
                          name="middle_name"
                          value={values.middle_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                      <div className={classes.nameFieldContainer}>
                        <TextField
                          className={classes.field}
                          placeholder="Enter Last Name"
                          variant="outlined"
                          name="last_name"
                          value={values.last_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                      {errors.last_name && touched.last_name && errors.last_name && (
                        <p className={classes.error}>{errors.last_name && touched.last_name && errors.last_name}</p>
                      )}
                    </div>
                    <div className={classes.column}>
                      <div className={classes.nameFieldContainer}>
                        <TextField
                          className={classes.field}
                          placeholder="Enter MRN"
                          variant="outlined"
                          name="mrn"
                          value={values.mrn}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    <div className={classes.sectionHead}>Patient Location</div>
                    <div className={classes.fieldContainer}>
                      <TextField
                        className={classes.field}
                        placeholder="Enter Unit #"
                        variant="outlined"
                        name="unit"
                        value={values.unit}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className={classes.fieldContainer}>
                      <TextField
                        className={classes.field}
                        placeholder="Enter Room #"
                        variant="outlined"
                        name="room"
                        value={values.room}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className={classes.sectionHead}>Patient Notifications</div>
                    <div className={classes.column}>
                      <div className={classes.nameFieldContainer}>
                        {filteredUsers && (
                          <Autocomplete
                            multiple
                            options={filteredUsers}
                            className={classes.field}
                            getOptionLabel={(option) => getLabel(option)}
                            id="auto-complete"
                            getOptionSelected={(option, value) => option.id === value.id}
                            autoComplete
                            value={selectedHCPs}
                            onChange={(event, newValue) => setSelectedHCPs(newValue)}
                            renderInput={(params) => <TextField {...params} label="Provider" margin="normal" />}
                          />
                        )}
                      </div>
                    </div>
                    <div className={classes.column}>
                      <div className={classes.nameFieldContainer}>
                        {groups && (
                          <Autocomplete
                            multiple
                            options={groups}
                            className={classes.field}
                            getOptionLabel={(option) => option.name}
                            id="auto-complete"
                            getOptionSelected={(option, value) => option.id === value.id}
                            autoComplete
                            value={selectedGroups}
                            onChange={(event, newValue) => setSelectedGroups(newValue)}
                            renderInput={(params) => <TextField {...params} label="Group" margin="normal" />}
                          />
                        )}
                      </div>
                    </div>
                    <div className={classes.buttonRow}>
                      <Button
                        className={classes.button}
                        size="small"
                        variant="contained"
                        type="submit"
                        disabled={isSubmitting || !hasNotifications}
                      >
                        Save Changes
                      </Button>
                      <Button className={classes.button} size="small" variant="contained" onClick={handleClose}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </MuiPickersUtilsProvider>
              );
            }}
          </Formik>
        </div>
      </Fade>
    </Modal>
    </>
  );
};

ExistingPatientModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default ExistingPatientModal;
